// Authentication Hook for MUU Platform
import { useCallback, useMemo } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import * as Sentry from '@sentry/react';
import {
  UseAuthReturn,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  TokenPayload,
} from '../types/auth';

/**
 * Custom hook for authentication management
 * Provides a convenient interface to the AuthContext
 */
export function useAuth(): UseAuthReturn {
  const authContext = useAuthContext();

  // Computed properties
  const isLoggedIn = useMemo(() => {
    return authContext.authState.isAuthenticated && !!authContext.authState.user;
  }, [authContext.authState.isAuthenticated, authContext.authState.user]);

  const userRole = useMemo(() => {
    return authContext.authState.user?.role || null;
  }, [authContext.authState.user?.role]);



  // Role checking functions
  const hasRole = useCallback((role: string): boolean => {
    if (!authContext.authState.user) return false;
    return authContext.authState.user.role === role;
  }, [authContext.authState.user]);

  const hasFarmRole = useCallback((farmId: string, role: string): boolean => {
    if (!authContext.authState.user?.farm_role_assignments) return false;
    
    return authContext.authState.user.farm_role_assignments.some(
      assignment => assignment.farm_id === farmId && assignment.role === role
    );
  }, [authContext.authState.user?.farm_role_assignments]);

  // Enhanced login with error handling
  const login = useCallback(async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await authContext.login(credentials);
      
      // Additional client-side validation if needed
      if (response.success && response.user) {
        // Update last activity
        // Could trigger analytics events here
  
      }
      
      return response;
    } catch (error) {
      Sentry.setTag('auth_action', 'login');
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      console.error('Login error in useAuth:', error);
      return {
        success: false,
        user: {} as any,
        tokens: {} as any,
        message: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }, [authContext.login]);

  // Login com Google removido (não utilizado)

  // Enhanced logout with cleanup
  const logout = useCallback(async (): Promise<void> => {
    try {
      await authContext.logout();
      
      // Additional cleanup if needed
      // Clear any cached data, analytics, etc.

    } catch (error) {
      console.error('Logout error in useAuth:', error);
      // Force logout even if API call fails
      authContext.clearAuthData();
    }
  }, [authContext.logout, authContext.clearAuthData]);

  // Token refresh with retry logic
  const refreshTokens = useCallback(async (): Promise<boolean> => {
    try {
      const success = await authContext.refreshTokens();
      
      if (success) {
  
      } else {
        console.warn('Token refresh failed');
      }
      
      return success;
    } catch (error) {
      console.error('Token refresh error in useAuth:', error);
      return false;
    }
  }, [authContext.refreshTokens]);

  // Profile update with optimistic updates
  const updateProfile = useCallback(async (updates: Partial<NonNullable<typeof authContext.authState.user>>): Promise<void> => {
    if (!authContext.authState.user) {
      throw new Error('No authenticated user');
    }

    try {
      await authContext.updateProfile(updates);

    } catch (error) {
      console.error('Profile update error in useAuth:', error);
      throw error;
    }
  }, [authContext.updateProfile, authContext.authState.user]);

  // Password change with validation
  const changePassword = useCallback(async (request: ChangePasswordRequest): Promise<void> => {
    // Client-side validation
    if (request.newPassword !== request.confirmPassword) {
      throw new Error('New passwords do not match');
    }

    if (request.newPassword.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }

    try {
      await authContext.changePassword(request);

    } catch (error) {
      console.error('Password change error in useAuth:', error);
      throw error;
    }
  }, [authContext.changePassword]);

  // Auth status check with automatic refresh
  const checkAuthStatus = useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await authContext.checkAuthStatus();
      
      if (!isValid) {
        console.warn('Authentication status check failed');
        // Optionally trigger logout
        await logout();
      }
      
      return isValid;
    } catch (error) {
      console.error('Auth status check error in useAuth:', error);
      return false;
    }
  }, [authContext.checkAuthStatus, logout]);

  // Get user permissions
  const getUserPermissions = useCallback((): string[] => {
    if (!authContext.authState.user) return [];
    
    const permissions: string[] = [];
    
    // Add role-based permissions
    switch (authContext.authState.user.role) {
      case 'admin':
        permissions.push('admin:all', 'user:all', 'farm:all');
        break;
      case 'manager':
        permissions.push('user:read', 'user:write', 'farm:read', 'farm:write');
        break;
      case 'user':
        permissions.push('user:read', 'farm:read');
        break;
      case 'professional':
        permissions.push('professional:all', 'appointment:all');
        break;
    }
    
    // Add farm-specific permissions
    if (authContext.authState.user.farm_role_assignments) {
      authContext.authState.user.farm_role_assignments.forEach(assignment => {
        permissions.push(`farm:${assignment.farm_id}:${assignment.role}`);
      });
    }
    
    return permissions;
  }, [authContext.authState.user]);

  // Check if user has specific permission
  const hasPermission = useCallback((permission: string): boolean => {
    const userPermissions = getUserPermissions();
    return userPermissions.includes(permission) || userPermissions.includes('admin:all');
  }, [getUserPermissions]);

  // Get token payload with error handling
  const getTokenPayload = useCallback((token?: string): TokenPayload | null => {
    try {
      return authContext.getTokenPayload(token);
    } catch (error) {
      console.error('Error getting token payload:', error);
      return null;
    }
  }, [authContext.getTokenPayload]);

  // Check if token is expired
  const isTokenExpired = useCallback((token?: string): boolean => {
    try {
      return authContext.isTokenExpired(token);
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true;
    }
  }, [authContext.isTokenExpired]);

  // Get user display name
  const getUserDisplayName = useCallback((): string => {
    if (!authContext.authState.user) return 'Guest';
    
    const user = authContext.authState.user;
    return user.full_name || user.email || 'User';
  }, [authContext.authState.user]);

  // Get user initials
  const getUserInitials = useCallback((): string => {
    if (!authContext.authState.user) return 'G';
    
    const user = authContext.authState.user;
    if (user.full_name) {
      const names = user.full_name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    
    return user.email[0].toUpperCase();
  }, [authContext.authState.user]);

  // Return the enhanced auth interface
  return {
    // State
    authState: authContext.authState,
    
    // Computed properties
    isLoggedIn,
    userRole,
    
    // Core auth functions
    login,
    logout,
    refreshTokens,
    
    // User management
    updateProfile,
    changePassword,
    checkUserProfile: authContext.checkUserProfile,
    
    // Auth utilities
    checkAuthStatus,
    clearAuthData: authContext.clearAuthData,
    isTokenExpired,
    getTokenPayload,
    
    // Role and permission checking
    hasRole,
    hasFarmRole,
    hasPermission,
    getUserPermissions,
    
    // User display utilities
    getUserDisplayName,
    getUserInitials,
    // Plan utilities
    refreshPlanStatus: authContext.refreshPlanStatus,
  };
}

export default useAuth;